// src/pages/admin/EventEditBanner.js

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import {
  getStorage,
  ref as storageRef,
  getDownloadURL,
  uploadBytes
} from 'firebase/storage';
import { db } from '../../firebase';

export default function EventEditBanner() {
  const { eventId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading]       = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    bannerFile:     null,   // 새로 업로드된 File 객체
    bannerPath:     '',     // Firestore에 저장된 “스토리지 경로” or HTTP URL
    bannerPreview:  '',     // 화면에 보여줄 이미지 URL(HTTP URL 또는 ObjectURL)
    title:          '',
    startDate:      '',
    endDate:        '',
  });

  // “YYYY.MM.DD” 혹은 Firestore Timestamp → input[type=date]용 “YYYY-MM-DD”로 포맷
  const formatForDateInput = val => {
    if (!val) return '';
    if (val.toDate) {
      return val.toDate().toISOString().slice(0, 10);
    }
    if (typeof val === 'string') {
      return val.includes('.') ? val.split('.').join('-') : val;
    }
    return '';
  };

  // ==============================
  // convertToWebp: PNG/JPEG 등 이미지를 WebP Blob으로 변환
  // ==============================
  const convertToWebp = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          canvas.width = img.width;
          canvas.height = img.height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0);
          canvas.toBlob(
            (blob) => {
              if (blob) {
                resolve(blob);
              } else {
                reject(new Error('WebP 변환 실패'));
              }
            },
            'image/webp',
            1.0 // 품질: 0.0 ~ 1.0 (0.8 권장)
          );
        };
        img.onerror = () => reject(new Error('이미지 로딩 실패'));
        img.src = reader.result;
      };
      reader.onerror = () => reject(new Error('FileReader 실패'));
      reader.readAsDataURL(file);
    });
  };

  // ==============================
  // uploadFileWithCache: Blob/File 객체 업로드 + Cache-Control 설정
  // ==============================
  const uploadFileWithCache = async (blobOrFile, folderPath, filename, contentType) => {
    const storage = getStorage();
    const fileRef = storageRef(storage, `${folderPath}/${filename}`);
    const metadata = {
      contentType,
      cacheControl: 'public, max-age=43200' // 12시간 캐시
    };
    await uploadBytes(fileRef, blobOrFile, metadata);
    const url = await getDownloadURL(fileRef);
    return url;
  };

  // ─────────────────────────────────────────────────────────
  // 1) Firestore에서 기존 이벤트 데이터 불러오기
  //    (bannerImageUrl 필드가 HTTP URL인지 스토리지 경로인지 판별하여 preview 설정)
  // ─────────────────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      try {
        const refDoc = doc(db, 'events', eventId);
        const snap = await getDoc(refDoc);
        if (!snap.exists()) {
          alert('해당 이벤트를 찾을 수 없습니다.');
          navigate('/admin', { state: { tab: 'events' } });
          return;
        }
        const data = snap.data();

        let previewUrl = '';
        const storedPath = data.bannerImageUrl || '';
        if (typeof storedPath === 'string' && storedPath.startsWith('http')) {
          // 이미 HTTP URL로 저장되어 있으면 그대로 사용
          previewUrl = storedPath;
        } else if (storedPath) {
          // 스토리지 경로이면 getDownloadURL 호출
          const storage = getStorage();
          try {
            previewUrl = await getDownloadURL(storageRef(storage, storedPath));
          } catch (err) {
            console.warn(`Storage에서 이벤트 배너 다운로드 URL 가져오기 실패 (${storedPath}):`, err);
            previewUrl = '';
          }
        }

        // input[type=date]에 넣기 위한 포맷 변환
        const startDate = formatForDateInput(data.startDate);
        const endDate   = formatForDateInput(data.endDate);

        setForm({
          bannerFile:     null,
          bannerPath:     storedPath,   // HTTP URL or 스토리지 경로
          bannerPreview:  previewUrl,   // 화면 프리뷰용 URL
          title:          data.title || '',
          startDate,
          endDate,
        });
      } catch (err) {
        console.error(err);
        alert('데이터 불러오기 중 오류가 발생했습니다.');
        navigate('/admin', { state: { tab: 'events' } });
      } finally {
        setLoading(false);
      }
    })();
  }, [eventId, navigate]);

  const clearForm = useCallback(() => {
    navigate('/admin', { state: { tab: 'events' } });
  }, [navigate]);

  const handleChange = e => {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: value }));
  };

  const handleFileChange = e => {
    const file = e.target.files?.[0];
    if (!file) return;
    setForm(f => ({
      ...f,
      bannerFile:    file,
      bannerPreview: URL.createObjectURL(file), // 즉시 ObjectURL로 미리보기
      bannerPath:    ''                         // 새 파일을 선택했으므로 기존 경로 초기화
    }));
  };

  // ─────────────────────────────────────────────────────────
  // 2) 폼 제출 시: 새로 업로드된 배너 파일이 있으면 convertToWebp → uploadFileWithCache 호출 → HTTP URL 반환
  //    Firestore 업데이트 시 bannerImageUrl 필드에 HTTP URL 저장
  // ─────────────────────────────────────────────────────────
  const handleSubmit = async e => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);

    // “YYYY-MM-DD” → “YYYY.MM.DD” 포맷 (Firestore에 저장될 형태)
    const saveStart = form.startDate ? form.startDate.split('-').join('.') : '';
    const saveEnd   = form.endDate   ? form.endDate.split('-').join('.')   : '';

    try {
      let newBannerUrl = form.bannerPath; // 기존에 저장된 URL(스토리지 경로 또는 HTTP URL)

      // 새로 업로드된 파일이 있으면 WebP 변환 후 uploadFileWithCache 사용 → HTTP URL 반환
      if (form.bannerFile) {
        if (form.bannerFile.type.startsWith('image/')) {
          // WebP로 변환
          const webpBlob = await convertToWebp(form.bannerFile);
          const webpFilename = `${eventId}_banner`;
          newBannerUrl = await uploadFileWithCache(
            webpBlob,
            `events/${eventId}`,
            webpFilename,
            'image/webp'
          );
        } else {
          // 이미지가 아닐 경우 원본 그대로 업로드
          const filename = `${eventId}_banner`;
          newBannerUrl = await uploadFileWithCache(
            form.bannerFile,
            `events/${eventId}`,
            filename,
            form.bannerFile.type
          );
        }
      }

      // Firestore에는 “bannerImageUrl” 필드에 HTTP URL 저장
      await updateDoc(doc(db, 'events', eventId), {
        bannerImageUrl: newBannerUrl,
        title:          form.title,
        startDate:      saveStart,
        endDate:        saveEnd,
      });

      alert('이벤트 배너 정보가 업데이트되었습니다.');
      navigate('/admin', { state: { tab: 'events' } });
    } catch (err) {
      console.error(err);
      alert('저장 중 오류가 발생했습니다.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="p-6 text-center text-white">로딩 중…</div>;
  }

  // ─────────────────────────────────────────────────────────
  // 3) D-Day 및 상태 계산 (기존 로직과 동일)
  // ─────────────────────────────────────────────────────────
  const today = new Date();
  const msPerDay = 1000 * 60 * 60 * 24;
  let status = '';
  let dDay = '';
  if (form.startDate && form.endDate) {
    const start = new Date(form.startDate + "T00:00:00");
    const end   = new Date(form.endDate   + "T00:00:00");
    if (today < start) {
      status = '진행예정';
      dDay   = Math.ceil((start - today) / msPerDay);
    } else if (today > end) {
      status = '종료';
    } else {
      status = '진행중';
      dDay   = Math.ceil((end - today) / msPerDay);
    }
  }

  return (
    <div className="min-h-screen bg-[#121212] text-gray-100 py-10">
      <div className="max-w-6xl mx-auto px-4 space-y-12">
        <h1 className="text-3xl font-bold text-center">이벤트 배너 편집</h1>

        <div className="bg-[#1a1a1a] rounded-2xl overflow-hidden shadow-2xl divide-x divide-neutral-700">
          <div className="flex flex-col lg:flex-row">

            {/* ─── Preview 영역 ─── */}
            <div className="flex-none p-8 flex justify-center">
              <div className="relative w-full max-w-[450px] rounded-2xl overflow-hidden flex flex-col shadow-lg">
                
                {/* 상태 배지 (상단 좌측) */}
                <div className="absolute top-4 left-4 flex space-x-2 z-10">
                  {!!status && (
                    <span className={`px-3 py-1 rounded-full text-sm font-semibold text-white ${
                      status === '진행중'
                        ? 'bg-green-600'
                        : status === '진행예정'
                        ? 'bg-yellow-600'
                        : 'bg-gray-600'
                    }`}>
                      {status}
                    </span>
                  )}
                  {status !== '종료' && dDay > 0 && dDay <= 3 && (
                    <span className="bg-red-600 text-white px-3 py-1 rounded-full text-sm font-semibold">
                      D-{dDay}
                    </span>
                  )}
                </div>

                {/* 16:9 배너 이미지 (aspect-banner) */}
                <div className="w-full aspect-banner bg-black overflow-hidden flex items-center justify-center">
                  {form.bannerPreview ? (
                    <img
                      src={form.bannerPreview}
                      alt="이벤트 배너 미리보기"
                      className="object-cover w-full h-full"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gray-700">
                      <span className="text-gray-500">배너 없음</span>
                    </div>
                  )}
                </div>

                {/* ─── 텍스트 영역 (제목 / 날짜) ─── */}
                <div className="p-6 bg-neutral-800 flex flex-col justify-between">
                  <h3 className="text-2xl font-bold text-white truncate mb-2">
                    {form.title || '제목 없음'}
                  </h3>
                  {form.startDate && form.endDate && (
                    <div className="text-base font-medium text-white/70">
                      {form.startDate.split('-').join('.')} ~ {form.endDate.split('-').join('.')}
                    </div>
                  )}
                </div>

              </div>
            </div>

            {/* ─── Form 영역 ─── */}
            <div className="flex-1 p-12">
              <form className="space-y-6" onSubmit={handleSubmit}>
                {/* 배너 이미지 파일 선택 */}
                <div className="flex items-center mb-4">
                  <label className="w-28 font-medium">배너 이미지</label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                    className="flex-1 bg-gray-700 text-gray-200 rounded px-3 py-2"
                    disabled={submitting}
                  />
                </div>

                {/* 제목 입력 */}
                <div className="flex items-center mb-4">
                  <label className="w-28 font-medium">제목</label>
                  <input
                    name="title"
                    value={form.title}
                    onChange={handleChange}
                    className="flex-1 bg-gray-700 text-white border border-gray-600 rounded px-3 py-2"
                    disabled={submitting}
                  />
                </div>

                {/* 시작일 입력 */}
                <div className="flex items-center mb-4">
                  <label className="w-28 font-medium">시작일</label>
                  <input
                    name="startDate"
                    type="date"
                    value={form.startDate}
                    onChange={handleChange}
                    className="flex-1 bg-gray-700 text-white border border-gray-600 rounded px-3 py-2"
                    disabled={submitting}
                  />
                </div>

                {/* 종료일 입력 */}
                <div className="flex items-center mb-6">
                  <label className="w-28 font-medium">종료일</label>
                  <input
                    name="endDate"
                    type="date"
                    value={form.endDate}
                    onChange={handleChange}
                    className="flex-1 bg-gray-700 text-white border border-gray-600 rounded px-3 py-2"
                    disabled={submitting}
                  />
                </div>

                {/* 버튼 그룹 */}
                <div className="flex justify-end gap-4 pt-4 border-t border-gray-700">
                  <button
                    type="button"
                    onClick={clearForm}
                    disabled={submitting}
                    className="bg-gray-600 text-white px-5 py-2 rounded hover:bg-gray-500 disabled:opacity-50"
                  >
                    취소
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    className={`px-6 py-2 rounded text-white ${
                      submitting ? 'bg-gray-600' : 'bg-blue-600 hover:bg-blue-700'
                    }`}
                  >
                    {submitting ? '저장중…' : '저장'}
                  </button>
                </div>
              </form>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
}
